// M4-A §1 Horizontal wells & laterals: plan (bench, length), geometry (KOP → curve → lateral, MD), drilling the curve
// and lateral (ROP × steering tool, geosteering in/out of zone, stuck pipe, kicks via the M2 mud window), manual or
// geosteerer steering, the gamma track. Hooks called from drilling.js / rotary.js are gated on well.lateral (only
// created from 2000), so M1–M3 play is untouched.
import { LATERAL, M4_FROM } from '../../data/m4/rules.js';
import { typicalLateralFt } from '../../data/m4/fields.js';
import { rigLateralLimitFt } from '../../data/m4/equipment.js';
import { eqDef, fieldDef, rigStats, stat, yf, eraDollars } from '../defs.js';
import { strataAt, stratumAt } from '../geo.js';
import { normal, chance } from '../../core/rng.js';
import { setProblem, touchToday, touchRopMult, touchWearMult, chargeFuel, wellRig, releaseRigImpl } from '../drilling.js';
import { rotaryRop, toothWearPerFt, bearingWearPerDay, effectiveStrata, chargeMud, stickPipe } from '../rotary.js';
import { ensureString, jointsFor, stockPipeFor, JOINT_FT } from '../pipe.js';
import { stockQty } from '../../core/state.js';
import { pressureRolls } from '../mud.js';
import { onKick } from '../wellcontrol.js';
import { hasMudMan } from '../automation.js';
import { traitCount } from '../crew.js';
import { notify, log, clamp, spend } from '../util.js';
import { MUD } from '../../data/m2/rules.js';
import { fracWellImpl } from './shaleFrac.js';
import { SHALE_SIM, gearDefs, gearIn, steerTool, guidance, hasGeosteerer, benchesAt, wellHash, eff, ok, no, padKey, round } from './shaleUtil.js';

// ---- limits & expectations ------------------------------------------------------------------------------------
export function lateralLimitFt(state, rig, fieldId) {
  const rigDef = rig ? eqDef(rig.defId) : null;
  const rigLim = rigLateralLimitFt(rigDef, rig ? gearDefs(state, rig) : []);
  const dsu = fieldDef(fieldId)?.shale?.maxLateralFt ?? Infinity;
  return Math.max(0, Math.min(rigLim, dsu));
}

// Expected in-zone fraction (spec §1.2): base by guidance + gear bonuses + geosteerer + upgrades − thin-window penalty.
export function expectedInZone(state, rig, windowFt = 50) {
  const g = guidance(state, rig);
  let x = LATERAL.inZoneBase[g] ?? 0.5;
  for (const d of rig ? gearDefs(state, rig) : []) x += stat(d, 'inZoneBonus', 0);
  if (hasGeosteerer(state, rig)) x += LATERAL.geosteererBonus;
  x += eff(state).inZoneBonus ?? 0;
  x -= 0.15 * Math.max(0, 1 - windowFt / LATERAL.windowFtRef);
  return clamp(x, 0.3, 0.99);
}

export function tortuosity(state, rig) {
  const tool = steerTool(state, rig) ?? 'motor';
  let t = LATERAL.tortuosityTorqueMult[tool] ?? 1;
  for (const d of rig ? gearDefs(state, rig) : []) t *= stat(d, 'tortuosityMult', 1);
  return t;
}

// Managed-pressure drilling gear widens the usable mud window (spec §1.2: mpd_system 0.6 ppg, dual gradient 1.2).
export function shaleMpdGain(state, well) {
  const rig = wellRig(state, well);
  const g = rig ? gearIn(state, rig, 'mpd') : null;
  return g ? stat(g, 'windowPpgGain', 0) : 0;
}
// Stuck-pipe chance for `ft` of lateral drilled today (per 1,000 ft × tortuosity: motors 1.3, RSS 1 × gear).
export const lateralStuckChance = (state, rig, ft) => Math.min(0.5, LATERAL.stuckPerKftLateral * (ft / 1000) * tortuosity(state, rig));
// Measured depth of a lateral well as drilled (water depth excluded; offshore M3 adds it to well.md).
export function lateralMd(well) {
  const L = well.lateral;
  if (!L) return well.depth;
  if (!L.phase) return well.depth;
  return L.kickoffMd + (L.curveDrilled ?? 0) + (L.lengthFt ?? 0);
}
export const plannedMd = (L) => L.kickoffMd + L.curveMd + L.targetFt;

// ---- planning --------------------------------------------------------------------------------------------------
// planLateral before the bit reaches the kickoff point. opts: { bench, lengthFt, spacingFt }
export function planLateralImpl(state, well, opts = {}) {
  if (yf(state) < M4_FROM) return no('Horizontal shale laterals come with the shale era (2000).');
  if (!well) return no('No such well.');
  if (well.ownerOperatorId || well.contract) return no('Contract wells follow the operator’s plan.');
  const fdef = fieldDef(well.fieldId);
  if (!fdef?.shale) return no('Laterals are planned in shale and tight-oil fields.');
  const L0 = well.lateral;
  if (L0?.phase) return no(`${well.name} has already kicked off — the lateral can't be re-planned.`);
  if (!['RIGGING_UP', 'DRILLING', 'PAUSED', 'TRIPPING', 'RUNNING_CASING', 'CEMENTING'].includes(well.status)) return no(`Can't plan a lateral while ${well.status.toLowerCase().replace(/_/g, ' ')}.`);
  const rig = wellRig(state, well);
  if (!rig) return no('Assign a rig first.');
  if (opts.lengthFt === 0 || opts.lengthFt === null) { // back to a vertical well
    if (L0) { well.lateral = null; well.target = L0.verticalTarget ?? well.target; }
    return ok(`${well.name}: vertical well.`);
  }
  const plot = state.fields[well.fieldId].plots[well.plot];
  const benches = benchesAt(fdef, plot);
  const bench = benches.find((r) => r.id === (opts.bench ?? L0?.bench)) ?? (opts.bench ? null : defaultBench(state, well, benches));
  if (!bench) return no(`No ${opts.bench ?? 'shale'} bench under this plot.`);
  const tool = steerTool(state, rig);
  if (!tool) return no(`${rig.name} needs a steerable mud motor or a rotary steerable system to build the curve.`);
  const limit = lateralLimitFt(state, rig, well.fieldId);
  const want = Math.round(opts.lengthFt ?? L0?.targetFt ?? Math.max(typicalLateralFt(fdef, yf(state)), 4000));
  if (want < SHALE_SIM.minLateralFt) return no(`A lateral shorter than ${SHALE_SIM.minLateralFt} ft isn't worth the curve.`);
  if (want > (fdef.shale.maxLateralFt ?? Infinity)) return no(`The spacing unit only allows a ${fdef.shale.maxLateralFt.toLocaleString('en-US')}-ft lateral.`);
  if (want > limit) return no(`${rig.name} can drill at most a ${limit.toLocaleString('en-US')}-ft lateral (torque & drag) — fit a rotary steerable or extended-reach pipe, or bring a bigger rig.`);
  const geo = lateralGeometry(state, well, bench, tool);
  const md = geo.kickoffMd + geo.curveMd + want;
  const rs = rigStats(eqDef(rig.defId));
  if (md + (well.waterDepthFt ?? 0) > rs.maxDepth) return no(`${rig.name} reaches ${rs.maxDepth.toLocaleString('en-US')} ft of hole; this well is ${Math.round(md).toLocaleString('en-US')} ft measured depth.`);
  if (well.depth > geo.kickoffMd + 1e-6) return no(`${well.name} is already below the kickoff point (${Math.round(geo.kickoffMd)} ft).`);
  const spacingFt = opts.spacingFt != null ? clamp(Math.round(opts.spacingFt), 100, fdef.shale.plotWidthFt) : (L0?.spacingFt ?? null);
  well.lateral = {
    bench: bench.id, benchName: bench.bench ?? bench.name, windowFt: bench.windowFt ?? 50, thick: bench.thick,
    kickoffMd: geo.kickoffMd, landingTvd: geo.landingTvd, windowTop: geo.windowTop, curveMd: geo.curveMd, bur: geo.bur, tool,
    targetFt: want, lengthFt: 0, curveDrilled: 0, inZoneFt: 0, inZoneFrac: null, phase: null, steer: L0?.steer ?? 'auto', pos: 0,
    dipSign: wellHash(state, well, 'dip') < 0.5 ? -1 : 1, gamma: [], stuckEvents: 0, sidetracks: 0,
    padUid: L0?.padUid ?? padKey(well.fieldId, well.plot), spacingFt, plannedDay: state.day, verticalTarget: L0?.verticalTarget ?? well.target,
    stages: null, proppantLbPerFt: null, stageSpacingFt: null, fluid: null, completedDay: null, parentOf: L0?.parentOf ?? [], childOf: L0?.childOf ?? [],
    design: L0?.design ?? null, batch: L0?.batch ?? false,
  };
  well.target = geo.kickoffMd;
  well.md = Math.round(well.depth);
  const reach = pipeReachFt(state, well);
  const short = reach < md ? ` Not enough drill pipe: what's on hand reaches about ${Math.round(reach).toLocaleString('en-US')} ft MD — buy ${jointsFor(md - reach).toLocaleString('en-US')} more joints before the lateral.` : '';
  return ok(`${well.name}: kick off at ${Math.round(geo.kickoffMd).toLocaleString('en-US')} ft, land in the ${well.lateral.benchName} at ${Math.round(geo.landingTvd).toLocaleString('en-US')} ft TVD and drill a ${want.toLocaleString('en-US')}-ft lateral (${Math.round(md).toLocaleString('en-US')} ft MD).${short}`);
}

// Measured depth this well's string can reach: its own joints plus stock not already spoken for by the other
// planned laterals still to be drilled (every rig draws on the one warehouse).
export function pipeReachFt(state, well) {
  const own = well.string?.joints ?? 0;
  const def = well.string?.defId ? eqDef(well.string.defId) : stockPipeFor(state);
  let free = def ? stockQty(state, def.id) : 0;
  for (const w of Object.values(state.wells)) {
    if (w === well || !w.lateral || w.lateral.completedDay != null || !w.rigId || (w.string?.defId && def && w.string.defId !== def.id)) continue;
    free -= Math.max(0, jointsFor(plannedMd(w.lateral)) - (w.string?.joints ?? 0));
  }
  return (own + Math.max(0, free)) * JOINT_FT;
}

// Pick the bench with the fewest wells on this pad (field order breaks ties).
export function defaultBench(state, well, benches) {
  if (!benches.length) return null;
  const key = padKey(well.fieldId, well.plot);
  const count = {};
  for (const w of Object.values(state.wells)) if (w !== well && w.lateral?.padUid === key) count[w.lateral.bench] = (count[w.lateral.bench] ?? 0) + 1;
  const mid = (r) => { const k = r.typeCurve?.ip30PerKft ?? [0, 0]; return (k[0] + k[1]) / 2; };
  return [...benches].sort((a, b) => (count[a.id] ?? 0) - (count[b.id] ?? 0) || mid(b) - mid(a))[0]; // emptiest bench, best rock first
}

// landing TVD inside the window, KOP above it, curve MD from the build rate (spec §1.1). Deterministic per well.
export function lateralGeometry(state, well, bench, tool) {
  const win = bench.windowFt ?? 50;
  const thick = bench.thick ?? win;
  const jitter = (wellHash(state, well, 'land') - 0.5) * Math.max(0, thick - win) * 0.5;
  const windowTop = Math.round((bench.top + Math.max(0, (thick - win) / 2) + jitter) * 10) / 10;
  const landingTvd = Math.round((windowTop + win / 2) * 10) / 10;
  const [k0, k1] = LATERAL.kickoffAboveTargetFt;
  const kickoffMd = Math.round(landingTvd - (k0 + (k1 - k0) * wellHash(state, well, 'kop')));
  const bur = LATERAL.buildRateDegPer100[tool] ?? 8;
  return { landingTvd, windowTop, kickoffMd, bur, curveMd: Math.round(LATERAL.curveMd(bur)) };
}

// Auto plan at spud (shale fields once laterals are the norm and the rig can build a curve). Returns the plan msg or null.
export function autoPlanLateral(state, well, rig) {
  const fdef = fieldDef(well.fieldId);
  if (!fdef?.shale || well.lateral || !steerTool(state, rig)) return null;
  const typical = typicalLateralFt(fdef, yf(state));
  if (typical <= 0) return null;
  const len = Math.min(typical, lateralLimitFt(state, rig, well.fieldId));
  if (len < SHALE_SIM.minLateralFt) return null;
  const r = planLateralImpl(state, well, { lengthFt: len });
  return r.ok ? r.msg : null;
}

// ---- steering ------------------------------------------------------------------------------------------------
export function steerImpl(state, well, dir) {
  if (!well?.lateral) return no('Not a horizontal well.');
  if (!['up', 'down', 'hold', 'auto'].includes(dir)) return no('Steer up, down or hold (or auto).');
  well.lateral.steer = dir;
  well.lateral.steerDay = state.day;
  return ok(dir === 'auto' ? `${well.name}: the geosteerer has the wheel.` : `${well.name}: steering ${dir}.`);
}
// Steering adjustment to today's in-zone draw: a geosteerer (or 'auto' with one) always corrects; a manual call
// toward the window helps as much as a geosteerer, away hurts; no call and no geosteerer: nothing.
function steerAdj(state, well, rig) {
  const L = well.lateral;
  const geo = hasGeosteerer(state, rig);
  if (L.steer === 'auto' || L.steer == null) return 0; // the geosteerer's bonus is already in expectedInZone
  const out = Math.abs(L.pos) > (L.windowFt / 2);
  const toward = L.pos > 0 ? 'down' : 'up'; // pos > 0 = above the window centre
  if (L.steer === 'hold') return out ? -0.03 : 0.01;
  const right = L.steer === toward;
  return (right ? (geo ? 0.02 : LATERAL.geosteererBonus) : -0.1);
}

// ---- hooks from drilling.js / rotary.js -----------------------------------------------------------------------
// reachTD on a lateral well still in the vertical section = we are at the kickoff point: start the curve.
export function onReachKop(state, well) {
  const L = well.lateral;
  if (!L || L.phase) return false;
  if (well.depth < L.kickoffMd - 1) return false;
  L.phase = 'curve';
  L.kickoffDay = state.day;
  well.target = L.landingTvd;
  well.status = 'DRILLING';
  well.problem = null;
  log(state, `${well.name}: kicked off at ${Math.round(well.depth).toLocaleString('en-US')} ft — building the curve at ${L.bur}°/100 ft.`, 'info', { wellId: well.id });
  return true;
}
// A bench passed in the vertical section of a lateral well is a show, not the completion zone.
export function onLateralPay(state, well, r) {
  if (!well.lateral) return false;
  log(state, `${well.name}: shows in the ${r.name ?? r.id} at ${Math.round(r.top).toLocaleString('en-US')} ft — drilling on to the kickoff point.`, 'info', { wellId: well.id });
  return true;
}
// STRAIGHTEN/sidetrack timer on a well drilling its lateral: lose lateral footage, not vertical depth.
export function lateralSidetrack(state, well, t) {
  const L = well.lateral;
  if (!L || (L.phase !== 'lateral' && L.phase !== 'curve') || t.data?.method !== 'sidetrack') return false;
  const loss = t.data.lossFt ?? 300;
  if (L.phase === 'lateral') {
    const keep = Math.max(0, L.lengthFt - loss);
    L.inZoneFt = L.lengthFt > 0 ? L.inZoneFt * keep / L.lengthFt : 0;
    L.lengthFt = keep;
    L.inZoneFrac = keep > 0 ? L.inZoneFt / keep : null;
    L.gamma = L.gamma.filter((g) => g.lat <= keep);
  } else L.curveDrilled = Math.max(0, L.curveDrilled - loss);
  L.sidetracks++;
  const extra = (well.cost ?? 0) * LATERAL.sidetrackCostMult;
  spend(state, extra, 'other', false);
  well.cost += extra;
  well.md = Math.round(lateralMd(well));
  well.deviationDeg = 1;
  well.status = 'DRILLING';
  well.problem = null;
  return true;
}
// Lateral footage per day improved year on year through the shale era (LATERAL.lateralRopByYear, interpolated).
function lateralYearMult(state) {
  const T = LATERAL.lateralRopByYear, y = yf(state);
  if (!T?.length) return 1;
  if (y <= T[0][0]) return T[0][1];
  for (let i = 1; i < T.length; i++) if (y <= T[i][0]) { const [y0, m0] = T[i - 1], [y1, m1] = T[i]; return m0 + (m1 - m0) * (y - y0) / (y1 - y0); }
  return T.at(-1)[1];
}

// ROP multiplier for lateral wells in the vertical section: batch drilling on a pad (spec §4) and missing M4 crew.
export function shaleRopMult(state, well, ctx) {
  const L = well.lateral;
  if (!L) return 1;
  let m = 1;
  const rig = ctx?.rig;
  if (rig) {
    const need = stat(ctx.rigDef, 'm4Roles', null);
    if (need) for (const [role, n] of Object.entries(need)) { const have = rig.crewIds.filter((id) => state.crew[id]?.role === role && state.crew[id].status === 'active').length; if (have < n) m *= SHALE_SIM.missingRolePenalty; }
  }
  if (!L.phase && L.batch) m /= 1 - (eff(state).batchSurfaceDaysSaved ?? 0.35);
  return m;
}

// ---- the curve & lateral drilling day (rotary.js dispatches here) -------------------------------------------
export function isLateralPhase(well) { const p = well.lateral?.phase; return p === 'curve' || p === 'lateral'; }

// M4 balance: shale-era directional gear (RSS, LWD, wired pipe, MPD) runs with service hands and rentals billed per
// day in the hole (stats.dayRate, base-year $; spec §17.3 "RSS $9k/d (2010 $)") — charged on curve & lateral days.
export function chargeGearDayRates(state, rig, well) {
  let amt = 0;
  for (const d of gearDefs(state, rig)) if (d.era === 'shale' && stat(d, 'dayRate', 0) > 0) amt += eraDollars(state, stat(d, 'dayRate', 0), stat(d, 'baseYear', d.year));
  if (amt > 0) { spend(state, amt, 'consumables', false); well.cost += amt; }
  return amt;
}
export function lateralDay(state, well, ctx) {
  const L = well.lateral;
  const rig = ctx.rig;
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId].plots[well.plot];
  const tool = steerTool(state, rig);
  if (!ctx.contractor && !tool) return setProblem(state, well, 'NO_STEER', 'no steerable motor or RSS on the rig — the curve and lateral need one.', { pause: true });
  const mdNow = lateralMd(well);
  if (!ctx.contractor) {
    if (!ensureString(state, well, mdNow + 300) && (well.string?.joints ?? 0) * 30 <= mdNow + 1) return setProblem(state, well, 'OUT_OF_PIPE', `out of drill pipe at ${Math.round(mdNow).toLocaleString('en-US')} ft MD — buy more joints.`, { pause: true });
    ctx.pipeDef = well.string ? eqDef(well.string.defId) : null;
  }
  well.problem = null;
  const strata = effectiveStrata(state, well, strataAt(fdef, plot));
  const touch = touchToday(state, well), tRop = touchRopMult(touch), tWear = touchWearMult(touch);
  const toolKey = tool ?? 'motor';
  const bit = ctx.bit;
  const auto = rig?.auto ?? { tripBelowPct: 15 };
  const tripPct = auto.tripBelowPct ?? 15;
  const wearBudget = ctx.contractor || !bit ? Infinity : (bit.condition > tripPct + 0.5 ? bit.condition - tripPct : Infinity);
  const oldHand = traitCount(ctx.cf.crew, 'old_hand') ? 0.8 : 1;
  const wob = well.wob ?? 1, rpm = well.rpm ?? 1;
  let time = 1, drilled = 0, wear = 0, latFt = 0, curveFt = 0;
  let guard = 0;
  while (time > 1e-6 && wear < wearBudget - 1e-9 && guard++ < 4) {
    const s = stratumAt(strata, well.depth + 0.01);
    const rop = rotaryRop(state, well, s, ctx) * tRop * (LATERAL.lateralRopMult[toolKey] ?? 0.7) * stat(ctx.bitDef, 'lateralRopMult', 1) * lateralYearMult(state);
    if (rop <= 1e-6) break;
    const wpf = ctx.contractor ? 0 : toothWearPerFt(state, ctx, s, wob, rpm, oldHand) * tWear;
    const byWear = wpf > 0 && wearBudget !== Infinity ? (wearBudget - wear) / wpf : Infinity;
    if (L.phase === 'curve') {
      const ft = Math.max(0, Math.min(rop * time, byWear, L.curveMd - L.curveDrilled));
      L.curveDrilled += ft; curveFt += ft; drilled += ft; time -= ft / rop; wear += ft * wpf;
      const th = (Math.PI / 2) * Math.min(1, L.curveDrilled / L.curveMd);
      well.depth = Math.round((L.kickoffMd + (L.landingTvd - L.kickoffMd) * Math.sin(th)) * 100) / 100;
      if (L.curveDrilled >= L.curveMd - 1e-6) land(state, well);
      else break;
    } else {
      const ft = Math.max(0, Math.min(rop * time, byWear, L.targetFt - L.lengthFt));
      if (ft <= 1e-6) break;
      steerFootage(state, well, rig, ft);
      latFt += ft; drilled += ft; time -= ft / rop; wear += ft * wpf;
      break;
    }
  }
  if (touch) well.touch = null;
  if (drilled > 0) {
    if (!ctx.contractor) {
      bit.condition = Math.max(0, bit.condition - wear);
      if (bit.bearing != null) bit.bearing = Math.max(0, bit.bearing - bearingWearPerDay(ctx, rpm));
      rig.condition = Math.max(0, rig.condition - 0.02);
      for (const c of ctx.cf.crew) c.xp += drilled / 10;
    }
    state.company.stats.ftDrilled += drilled;
    state.company.stats.lateralFt = (state.company.stats.lateralFt ?? 0) + latFt;
  }
  well.md = Math.round(lateralMd(well) + (well.offshore ? well.waterDepthFt ?? 0 : 0));
  if (!ctx.contractor) { chargeFuel(state, rig, well); chargeGearDayRates(state, rig, well); }
  const mudMan = hasMudMan(state, rig);
  const pr = pressureRolls(state, well, { drilling: true, ftToday: drilled, mudMan });
  if (drilled > 0 || pr.loss) chargeMud(state, well, Math.max(drilled, pr.loss ? 100 : 0), { lostMult: pr.loss ? MUD.lostCircMudCostMult : 1, mudMan });
  if (pr.loss) lostCirc(state, well, pr.window);
  if (pr.kick) { onKick(state, well, pr.kick); return; }
  if (pr.stuck) return stickPipe(state, well, 'stuck in the lateral where the mud is breaking down the rock');
  // torque & drag: stuck pipe per 1,000 ft of lateral (× motor tortuosity)
  if (latFt > 0 && !state._rush && chance(state, lateralStuckChance(state, rig, latFt))) {
    L.stuckEvents++;
    return stickPipe(state, well, `the string hung up ${Math.round(L.lengthFt).toLocaleString('en-US')} ft out in the lateral`);
  }
  if (L.phase === 'lateral' && L.lengthFt >= L.targetFt - 1e-6 && finishLateral(state, well)) fracWellImpl(state, well); // rig auto-complete: on the frac schedule
}

function lostCirc(state, well, w) {
  const l = (well.losses ??= { day: -999, n: 0 });
  l.n++;
  if (state.day - l.day > 10) notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'LOST_CIRC', pause: true, level: 'warn',
    text: `${well.name}: lost circulation in the lateral — ECD ${w.ecd.toFixed(1)} ppg against a ${w.frac.toFixed(1)} frac gradient. Lighten the mud.` });
  l.day = state.day;
}

// Geosteering: today's footage in/out of the window, the position track and the gamma log.
export function steerFootage(state, well, rig, ft) {
  const L = well.lateral;
  const exp = expectedInZone(state, rig, L.windowFt);
  const dayIn = clamp(exp + steerAdj(state, well, rig) + normal(state, 0, 0.05), 0.3, 0.99);
  // the bench drifts against the plan (dip): the bit wanders out on the dipSign side as in-zone falls
  const half = L.windowFt / 2;
  L.pos = Math.round(L.dipSign * half * (dayIn >= 0.9 ? (dayIn - 0.9) * 5 : 1 + (0.9 - dayIn) * 2) * (normal(state, 0, 1) > -1.5 ? 1 : -1) * 10) / 10;
  L.inZoneFt += ft * dayIn;
  L.lengthFt = Math.round((L.lengthFt + ft) * 10) / 10;
  L.inZoneFrac = round(L.inZoneFt / L.lengthFt, 4);
  L.gamma.push({ lat: Math.round(L.lengthFt), md: Math.round(lateralMd(well)), inZone: round(dayIn, 3), pos: L.pos, api: Math.round(60 + 90 * (1 - dayIn)) });
  if (L.gamma.length > 200) L.gamma.splice(0, L.gamma.length - 200);
  if (L.steer !== 'auto' && L.steer !== 'hold' && L.steerDay != null && state.day - L.steerDay > 0) L.steer = 'auto'; // a manual up/down call lasts a day
}

function land(state, well) {
  const L = well.lateral;
  L.phase = 'lateral';
  L.landedDay = state.day;
  well.depth = L.landingTvd;
  well.target = L.landingTvd + 1; // depth (TVD) stays at landing while the lateral is drilled: casing/kill handlers must resume DRILLING
  if (!well.revealed.includes(L.bench)) well.revealed.push(L.bench);
  log(state, `${well.name}: landed in the ${L.benchName} at ${Math.round(L.landingTvd).toLocaleString('en-US')} ft TVD — drilling the lateral.`, 'info', { wellId: well.id });
}

// Lateral at TD: the well is drilled and uncompleted (DUC) until the frac. The rig moves on.
export function finishLateral(state, well) {
  const L = well.lateral;
  L.phase = 'done';
  L.drilledDay = state.day;
  L.inZoneFrac = L.lengthFt > 0 ? round(L.inZoneFt / L.lengthFt, 4) : 0;
  const fdef = fieldDef(well.fieldId);
  const bench = benchesAt(fdef, state.fields[well.fieldId].plots[well.plot]).find((r) => r.id === L.bench);
  well.reservoirId = L.bench;
  well.payTop = bench?.top ?? L.windowTop;
  well.payFt = bench?.thick ?? L.windowFt;
  well.strikeDay ??= state.day;
  well.status = 'AT_TD';
  well.target = well.depth;
  well.problem = null;
  well.duc = { since: state.day };
  state.company.stats.strikes++;
  state.company.stats.horizontalWells = (state.company.stats.horizontalWells ?? 0) + 1;
  const rig = wellRig(state, well);
  const autoComplete = rig?.auto?.complete ?? true;
  notify(state, 'strike', { wellId: well.id, fieldId: well.fieldId, lateral: true, level: 'major', pause: !autoComplete,
    text: `${well.name}: lateral TD — ${Math.round(L.lengthFt).toLocaleString('en-US')} ft in the ${L.benchName}, ${Math.round(100 * L.inZoneFrac)}% in zone (${Math.round(well.md ?? lateralMd(well)).toLocaleString('en-US')} ft MD). Drilled, uncompleted: design the frac.` });
  if (rig) {
    rig.m4Last = { fieldId: well.fieldId, plot: well.plot, day: state.day };
    releaseRigImpl(state, rig);
  }
  return autoComplete;
}

// Small helpers the views use
export function lateralPoints(well, n = 24) {
  const L = well.lateral;
  if (!L) return [];
  const pts = [{ x: 0, tvd: 0 }, { x: 0, tvd: Math.min(well.depth, L.kickoffMd) }];
  const span = L.landingTvd - L.kickoffMd;
  const built = L.phase ? Math.min(1, (L.curveDrilled ?? 0) / L.curveMd) : 0;
  for (let i = 1; i <= n && built > 0; i++) {
    const th = (Math.PI / 2) * built * i / n;
    pts.push({ x: Math.round(span * (1 - Math.cos(th))), tvd: Math.round(L.kickoffMd + span * Math.sin(th)) });
  }
  if (L.lengthFt > 0) pts.push({ x: Math.round(span + L.lengthFt), tvd: L.landingTvd });
  return pts;
}
